import React, { useState, useEffect, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useSearchParams } from 'react-router-dom';
import Legend from '../components/Legend/Legend';
import MapViewer from '../components/MapViewer/MapViewer';
import { MapIcon, GlobeIcon, LayersIcon, CloseIcon } from '../components/Icon';
import styles from './Pages.module.css';

const PublicMapPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isInitialLoaded, setIsInitialLoaded] = useState(false);

    const [plotLegendData, setPlotLegendData] = useState([]);
    const [buildingLegendData, setBuildingLegendData] = useState([]);
    const [roadLegendData, setRoadLegendData] = useState([]);
    const [markerLegendData, setMarkerLegendData] = useState([]);

    const [activePlotFilters, setActivePlotFilters] = useState([]);
    const [activeBuildingFilters, setActiveBuildingFilters] = useState([]);
    const [activeRoadFilters, setActiveRoadFilters] = useState([]);
    const [activeMarkerFilters, setActiveMarkerFilters] = useState([]);
    const [activeStatusFilters, setActiveStatusFilters] = useState(['for_sale', 'is_building', 'is_planned', 'no_status']);
    
    const [mapStyleId, setMapStyleId] = useState('custom');
    const [showStatusIcons, setShowStatusIcons] = useState(true);
    const [mapData, setMapData] = useState(null);
    const [selectedFeature, setSelectedFeature] = useState(null);
    const mapRef = useRef(null);
    const previousViewState = useRef(null);

    const mapStyleNames = { basic: 'Схема', satellite: 'Спутник', custom: 'Своя' };
    const mapStyleIcons = {
        basic: ReactDOMServer.renderToStaticMarkup(<MapIcon size={18} />),
        satellite: ReactDOMServer.renderToStaticMarkup(<GlobeIcon size={18} />),
        custom: ReactDOMServer.renderToStaticMarkup(<LayersIcon size={18} />)
    };

    const handleMapStyleChange = () => {
        const currentIndex = mapStyles.indexOf(mapStyleId);
        const nextIndex = (currentIndex + 1) % mapStyles.length;
        setMapStyleId(mapStyles[nextIndex]);
    };

    useEffect(() => {
        const fetchLegend = async () => {
            try {
                const response = await fetch('/api/legend');
                const legend = await response.json();
                
                const plots = legend.plots || [];
                const buildings = legend.buildings || [];
                const roads = legend.roads || [];
                const markers = legend.markers || [];
                const statuses = ['for_sale', 'is_building', 'is_planned', 'no_status'];

                setPlotLegendData(plots);
                setBuildingLegendData(buildings);
                setRoadLegendData(roads);
                setMarkerLegendData(markers);

                const loadParam = (key, allList) => {
                    const paramValue = searchParams.get(key);
                    if (paramValue !== null) {
                        return paramValue ? paramValue.split(',') : [];
                    } else {
                        return allList.map(item => item.id || item);
                    }
                };

                setActivePlotFilters(loadParam('plots', plots));
                setActiveBuildingFilters(loadParam('buildings', buildings));
                setActiveRoadFilters(loadParam('roads', roads));
                setActiveMarkerFilters(loadParam('markers', markers));
                setActiveStatusFilters(loadParam('status', statuses));

                setIsInitialLoaded(true);
            } catch (error) { 
                console.error(error); 
                setIsInitialLoaded(true);
            }
        };
        fetchLegend();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchMapData = async () => {
            try {
                const [mapRes, legendRes] = await Promise.all([fetch('/api/map'), fetch('/api/legend')]);
                if (mapRes.ok && legendRes.ok) {
                    const data = await mapRes.json();
                    const legend = await legendRes.json();
                    const categoryProperties = {};
                    Object.values(legend).flat().forEach(cat => {
                        if (cat.id) categoryProperties[cat.id] = { color: cat.color || '#cccccc' };
                    });
                    const updatedFeatures = (data.features || []).map(feature => {
                        const categoryId = feature.properties.category;
                        let newProperties = { ...feature.properties };
                        if (categoryId && categoryProperties[categoryId]) {
                            newProperties.color = categoryProperties[categoryId].color;
                        }
                        return { ...feature, properties: newProperties };
                    });
                    setMapData({ ...data, features: updatedFeatures });
                }
            } catch (error) { console.error(error); }
        };
        fetchMapData();
    }, []);

    // Синхронизация выбранных фильтров в URL (красивый и короткий URL)
    useEffect(() => {
        if (!isInitialLoaded) return;

        const newParams = new URLSearchParams(searchParams);

        const syncParam = (key, activeList, allList) => {
            const isAllSelected = allList.length > 0 && 
                                  allList.length === activeList.length && 
                                  allList.every(item => activeList.includes(item.id || item));
            
            if (isAllSelected) {
                newParams.delete(key);
            } else {
                newParams.set(key, activeList.join(','));
            }
        };

        syncParam('plots', activePlotFilters, plotLegendData);
        syncParam('buildings', activeBuildingFilters, buildingLegendData);
        syncParam('roads', activeRoadFilters, roadLegendData);
        syncParam('markers', activeMarkerFilters, markerLegendData);
        
        const allStatuses = ['for_sale', 'is_building', 'is_planned', 'no_status'];
        syncParam('status', activeStatusFilters, allStatuses);

        setSearchParams(newParams, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePlotFilters, activeBuildingFilters, activeRoadFilters, activeMarkerFilters, activeStatusFilters, plotLegendData, buildingLegendData, roadLegendData, markerLegendData, isInitialLoaded]);

    // Синхронизация открытого/выбранного объекта в URL
    useEffect(() => {
        if (!isInitialLoaded) return;

        const newParams = new URLSearchParams(searchParams);
        if (selectedFeature) {
            newParams.set('feature', selectedFeature.id);
        } else {
            newParams.delete('feature');
        }
        setSearchParams(newParams, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFeature, isInitialLoaded]);

    // Автоматическое открытие и фокусировка объекта по параметру "feature" при загрузке данных карты
    useEffect(() => {
        if (mapData && isInitialLoaded) {
            const featureId = searchParams.get('feature');
            if (featureId) {
                const found = mapData.features.find(f => String(f.id) === featureId);
                if (found && (!selectedFeature || String(selectedFeature.id) !== featureId)) {
                    // Даем карте немного времени полностью прогрузиться, если это начальная загрузка
                    const timer = setTimeout(() => {
                        handleFeatureSelect(found);
                    }, 800);
                    return () => clearTimeout(timer);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapData, isInitialLoaded]);

    const handleToggleCategory = (id, category) => {
        const norm = category === 'участки' ? 'plots' : 
                     category === 'здания' ? 'buildings' : 
                     category === 'линии' || category === 'дороги' ? 'roads' : 
                     category === 'метки' ? 'markers' : category;
        const setter = norm === 'plots' ? setActivePlotFilters :
                       norm === 'buildings' ? setActiveBuildingFilters :
                       norm === 'roads' ? setActiveRoadFilters : setActiveMarkerFilters;
        setter(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
    };

    const handleSelectAll = (category) => {
        if (!category || category === 'all') {
            setActivePlotFilters(plotLegendData.map(p => p.id));
            setActiveBuildingFilters(buildingLegendData.map(b => b.id));
            setActiveRoadFilters(roadLegendData.map(r => r.id));
            setActiveMarkerFilters(markerLegendData.map(m => m.id));
            setActiveStatusFilters(['for_sale', 'is_building', 'is_planned', 'no_status']);
            return;
        }

        const norm = category === 'участки' ? 'plots' : 
                     category === 'здания' ? 'buildings' : 
                     category === 'линии' || category === 'roads' ? 'roads' : 
                     category === 'метки' ? 'markers' : category;

        if (norm === 'plots') setActivePlotFilters(plotLegendData.map(p => p.id));
        if (norm === 'buildings') setActiveBuildingFilters(buildingLegendData.map(b => b.id));
        if (norm === 'roads') setActiveRoadFilters(roadLegendData.map(r => r.id));
        if (norm === 'markers') setActiveMarkerFilters(markerLegendData.map(m => m.id));
    };

    const handleClearAll = (category) => {
        if (!category || category === 'all') {
            setActivePlotFilters([]);
            setActiveBuildingFilters([]);
            setActiveRoadFilters([]);
            setActiveMarkerFilters([]);
            setActiveStatusFilters([]);
            return;
        }

        const norm = category === 'участки' ? 'plots' : 
                     category === 'здания' ? 'buildings' : 
                     category === 'линии' || category === 'roads' ? 'roads' : 
                     category === 'метки' ? 'markers' : category;

        if (norm === 'plots') setActivePlotFilters([]);
        if (norm === 'buildings') setActiveBuildingFilters([]);
        if (norm === 'roads') setActiveRoadFilters([]);
        if (norm === 'markers') setActiveMarkerFilters([]);
    };

    const handleToggleStatus = (status) => {
        setActiveStatusFilters(prev => {
            if (prev.includes(status)) {
                return prev.filter(s => s !== status);
            } else {
                return [...prev, status];
            }
        });
    };

    const handleFeatureSelect = (feature) => {
        const map = mapRef.current?.getMap();
        if (map) {
            // Сохраняем текущее состояние карты перед полетом к объекту, если ничего не было выбрано
            if (!selectedFeature) {
                previousViewState.current = {
                    center: map.getCenter(),
                    zoom: map.getZoom(),
                    pitch: map.getPitch(),
                    bearing: map.getBearing()
                };
            }

            setSelectedFeature(feature);
            
            try {
                const turf = require('@turf/turf');
                const bbox = turf.bbox(feature);
                map.fitBounds(bbox, { padding: 100, duration: 1000 });
            } catch (e) {
                console.error("Error fitting bounds", e);
                map.flyTo({ center: feature.geometry.coordinates, zoom: 16 });
            }
        }
    };

    const handleCloseInfo = () => {
        setSelectedFeature(null);
        const map = mapRef.current?.getMap();
        // Возвращаем карту в исходное состояние, если оно было сохранено
        if (map && previousViewState.current) {
            map.flyTo({
                center: previousViewState.current.center,
                zoom: previousViewState.current.zoom,
                pitch: previousViewState.current.pitch,
                bearing: previousViewState.current.bearing,
                duration: 1000
            });
            previousViewState.current = null;
        }
    };

    const mapStyles = ['basic', 'satellite', 'custom'];

    return (
        <div className={styles.publicMapPage}>
            <main className={styles.mapContainer}>
                <Legend
                    activePlotFilters={activePlotFilters}
                    activeBuildingFilters={activeBuildingFilters}
                    activeRoadFilters={activeRoadFilters}
                    activeMarkerFilters={activeMarkerFilters}
                    onToggleCategory={handleToggleCategory}
                    onSelectAll={handleSelectAll}
                    onClearAll={handleClearAll}
                    showStatusIcons={activeStatusFilters.length === 4}
                    activeStatusFilters={activeStatusFilters}
                    onToggleShowStatusIcons={() => {
                        if (activeStatusFilters.length === 4) {
                            setActiveStatusFilters([]);
                            setShowStatusIcons(false);
                        } else {
                            setActiveStatusFilters(['for_sale', 'is_building', 'is_planned', 'no_status']);
                            setShowStatusIcons(true);
                        }
                    }}
                    onToggleStatusFilter={handleToggleStatus}
                    plotLegendData={plotLegendData}
                    buildingLegendData={buildingLegendData}
                    roadLegendData={roadLegendData}
                    markerLegendData={markerLegendData}
                    floating={true}
                />
                <MapViewer
                    mapStyleId={mapStyleId}
                    styleSwapControlProps={{
                        onClick: handleMapStyleChange,
                        title: `Стиль: ${mapStyleNames[mapStyleId]}`,
                        iconSvg: mapStyleIcons[mapStyleId]
                    }}
                    showDrawingTools={false}
                    mapData={mapData}
                    mapRef={mapRef}
                    onFeatureSelect={handleFeatureSelect}
                    focusedFeature={selectedFeature}
                    legendData={{ plots: plotLegendData, buildings: buildingLegendData, roads: roadLegendData, markers: markerLegendData }}
                    activeFilters={{ plots: activePlotFilters, buildings: activeBuildingFilters, roads: activeRoadFilters, markers: activeMarkerFilters, status: activeStatusFilters }}
                    showStatusIcons={showStatusIcons}
                />
                {selectedFeature && (
                    <div className={styles.featureInfoCard}>
                        <button className={styles.featureInfoCloseBtn} onClick={handleCloseInfo}><CloseIcon size={20} /></button>
                        
                        <div className={styles.featureInfoScroll}>
                            {(() => {
                                // Универсальная функция для получения массива из свойства (парсит если строка)
                                const getArray = (val) => {
                                    if (!val) return [];
                                    if (Array.isArray(val)) return val;
                                    try { return JSON.parse(val); } catch (e) { return []; }
                                };

                                const photos = getArray(selectedFeature.properties.photos);
                                const links = getArray(selectedFeature.properties.links);
                                const customFields = getArray(selectedFeature.properties.custom_fields);

                                return (
                                    <>
                                        {/* Фотографии */}
                                        {photos.length > 0 && (
                                            <div className={styles.featurePhotos}>
                                                {photos.map((photo, idx) => (
                                                    <div key={idx} className={styles.photoWrapper}>
                                                        <img src={photo.url} alt={photo.caption || 'Фото объекта'} />
                                                        {photo.caption && <div className={styles.photoCaption}>{photo.caption}</div>}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className={styles.featureInfoMain}>
                                            <h2>{selectedFeature.properties.name || selectedFeature.properties.cadastral_number || 'Объект'}</h2>
                                            
                                            <div className={styles.statusBadges}>
                                                {selectedFeature.properties.for_sale && <span className={`${styles.badge} ${styles.forSale}`}>В продаже</span>}
                                                {selectedFeature.properties.is_building && <span className={`${styles.badge} ${styles.isBuilding}`}>Строится</span>}
                                                {selectedFeature.properties.is_planned && <span className={`${styles.badge} ${styles.isPlanned}`}>Запланировано</span>}
                                            </div>

                                            <div className={styles.mainProps}>
                                                {selectedFeature.properties.cadastral_number && (
                                                    <div className={styles.propRow}>
                                                        <span className={styles.propLabel}>Кадастровый номер:</span>
                                                        <span className={styles.propValue}>{selectedFeature.properties.cadastral_number}</span>
                                                    </div>
                                                )}
                                                {selectedFeature.properties.area && (
                                                    <div className={styles.propRow}>
                                                        <span className={styles.propLabel}>Площадь:</span>
                                                        <span className={styles.propValue}>{selectedFeature.properties.area} м²</span>
                                                    </div>
                                                )}
                                            </div>

                                            {selectedFeature.properties.description && (
                                                <div className={styles.infoSection}>
                                                    <h4>Информация</h4>
                                                    <p>{selectedFeature.properties.description}</p>
                                                </div>
                                            )}

                                            {customFields.length > 0 && (
                                                <div className={styles.infoSection}>
                                                    <h4>Характеристики</h4>
                                                    <div className={styles.customFieldsGrid}>
                                                        {customFields.map((field, idx) => (
                                                            <div key={idx} className={styles.customField}>
                                                                <div className={styles.fieldName}>{field.key}</div>
                                                                <div className={styles.fieldValue}>{field.value}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {links.length > 0 && (
                                                <div className={styles.infoSection}>
                                                    <h4>Ссылки</h4>
                                                    <div className={styles.linksList}>
                                                        {links.map((link, idx) => (
                                                            <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className={styles.featureLink}>
                                                                {link.text || 'Перейти'}
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default PublicMapPage;
